// GEO audit handoff card — the "WHAT TO SAY" block for /geo-audit.
//
// Sibling of WhatsappHandoffCard. Rendered by AssistantBubble when a message
// carries the `[[geo-handoff:<base64 json>]]` marker. Reuses the same handoff__*
// styles, so it looks and behaves exactly like the sample-deploy card.
//
// Same safety philosophy as the sample card: the MESSAGE TEXT IS HARDCODED here
// and the agent supplies only measured values plus two operator inputs (the
// trade, and the one-line leak). The agent can therefore never garble the sales
// script -- it can only fill in blanks, and every number comes from the run.
//
// TWO MESSAGES (2026-08-25). The offer close moved to the playbook: it is
// settled copy that never varies with the run, so keeping it here only gave it
// a second place to drift. The PDF is still surfaced, because the close attaches
// it. The pacing is what makes the play work -- the qualifier leaks ONE visceral
// true fact and asks; the score is the payoff for saying yes, and only lands
// once they have.
//
// EVERY STRING BELOW IS MIRRORED IN scripts/skills/geo-audit/build_handoff.py.
// That script writes HANDOFF.md, the fallback the operator reads when the card
// cannot render. If you change wording here, change it there in the same commit
// or the card and the fallback will tell the same client two different things.

import { useRef, useState } from "react";
import { copyTextToClipboard } from "../lib/clipboard";

export interface GeoHandoffFirm {
  name: string;
  /** How many answers named this firm. Drives "10 each" vs "at 10 and ... 7". */
  count?: number;
}

export interface GeoHandoffData {
  named: number;
  slots: number;
  queries: number;
  firms: GeoHandoffFirm[];
  /** Mentions across ALL real competitor firms, not just the two shown. Absent
   *  when the run could not prove it (no competitor_mentions.json) — the clause
   *  is then dropped rather than guessed, because it goes to a paying client. */
  competitorTotal?: number | null;
  /** The client's trade, singular, as it reads after "when they need a ". */
  trade?: string;
  /** The single leak for the qualifier. Empty when the site is already built,
   *  in which case there is no qualifier at all. */
  leak?: string;
  /** A real Type A question from this run, quoted back in the qualifier. */
  query?: string;
  /** Site already live → no "while building your site", so message 2 goes out
   *  standalone and carries the framing itself. */
  alreadyBuilt?: boolean;
  /** Engines this run ACTUALLY asked, from the matrix columns. Absent on
   *  markers predating 2026-08-25, which were always three-engine runs. */
  engines?: string[];
  /** Set when the measured data undercuts the pitch — the client scores well,
   *  so the message tells them they are winning while the close asks them to
   *  pay to fix it. build_handoff.py refuses unless --allow-strong-score, so
   *  this is only ever present because Julian overrode it deliberately. */
  weakPitch?: string | null;
  image?: string;
  pdf?: string;
  /** Absolute paths under the inbox, so the chat can actually serve them.
   *  A path in the geo-run folder renders but 404s on tap -- the uploads route
   *  only resolves inside INBOX_DIR. build_handoff.py publishes copies there. */
  imagePath?: string;
  pdfPath?: string;
  /** Public URL of the report on the client's own site. When present this is
   *  what the salesperson sends -- a link the client opens on any device,
   *  instead of a 600KB download the salesperson has to re-attach by hand. */
  pdfUrl?: string;
}

/** Decode the marker payload. Returns null on anything malformed — a broken
 *  payload must degrade to plain text, never to a half-rendered sales script. */
export function decodeGeoHandoff(raw: string): GeoHandoffData | null {
  try {
    const norm = raw.replace(/-/g, "+").replace(/_/g, "/");
    // atob gives latin-1 bytes; decode them as UTF-8 so firm names keep any
    // accents rather than arriving mojibaked into a client-facing message.
    const bytes = Uint8Array.from(atob(norm), (c) => c.charCodeAt(0));
    const json = new TextDecoder("utf-8").decode(bytes);
    const d = JSON.parse(json) as GeoHandoffData;
    if (
      typeof d.named !== "number" ||
      typeof d.slots !== "number" ||
      typeof d.queries !== "number" ||
      !Array.isArray(d.firms) ||
      d.firms.length === 0 ||
      d.firms.some((f) => !f || typeof f.name !== "string" || !f.name.trim())
    ) {
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

/** Strip WhatsApp formatting characters out of interpolated text.
 *
 *  The messages use *bold* deliberately — "*2*", "*38 times*", the quoted
 *  question. WhatsApp pairs those markers across the WHOLE message, so one
 *  stray `*` inside a leak, a question or a firm name makes the count odd and
 *  the formatting bleeds through everything after it. Measured: a leak reading
 *  "one of them said *general firm" left the rest of the message bolded in the
 *  client's chat. `_`, `~` and backticks are the same hazard. */
function waSafe(text: string): string {
  return text.replace(/[*_~`]/g, "");
}

/** "a microcement contractor" / "an interior contractor". */
function article(phrase: string): string {
  return /^[aeiou]/i.test(phrase.trim()) ? "an" : "a";
}

/** Names only — never a URL. Naming a rival stings, which is the point; linking
 *  one sends the client off to browse someone who is beating him, one tap from
 *  admiring their site instead of reading the offer. */
function firmsClause(firms: GeoHandoffFirm[]): string {
  const names = firms.map((f) => waSafe(f.name));
  const counts = firms.map((f) => f.count ?? 0);
  if (names.length === 0) return "";
  if (names.length === 1) {
    return counts[0] ? `mostly ${names[0]}, named ${counts[0]} times.` : `mostly ${names[0]}.`;
  }
  const joined =
    names.length === 2
      ? names.join(" and ")
      : names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
  // "10 each" only reads as true when both really are 10.
  if (counts[0] && counts.every((c) => c === counts[0])) return `mostly ${joined}, ${counts[0]} each.`;
  if (counts.every((c) => c > 0)) {
    const pairs = firms.map((f) => `${waSafe(f.name)} at ${f.count}`);
    const tail =
      pairs.length === 2
        ? pairs.join(" and ")
        : pairs.slice(0, -1).join(", ") + " and " + pairs[pairs.length - 1];
    return `mostly ${tail}.`;
  }
  return `mostly ${joined}.`;
}

/** "ChatGPT, Gemini and Claude" — the qualifier names them. Never assume three:
 *  a Claude-only run must not claim a measurement that did not happen. */
function engineList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "ChatGPT";
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

/** "all three" / "both" / "Claude" — message 2 refers back to them. */
function engineShorthand(names: string[]): string {
  if (names.length >= 3) return "all three";
  if (names.length === 2) return "both";
  return names[0] ?? "ChatGPT";
}

function enginesOf(d: GeoHandoffData): string[] {
  return d.engines?.length ? d.engines : ["ChatGPT", "Gemini", "Claude"];
}

function scoreClause(d: GeoHandoffData): string {
  // Zero gets its own sentence — "you were named in *0*" is the awkward phrasing
  // of the single best case this pitch has, and reads like a failed merge field.
  let line = d.named ? `you were named in *${d.named}*.` : `you weren't named once.`;
  if (d.competitorTotal) {
    line += ` your competitors were named *${d.competitorTotal} times* in the same answers.`;
  }
  return line;
}

/** The qualifier. Framing + engines + ONE leak + the ask. No numbers, ever.
 *
 *  Returns "" unless every part is present, and the card renders the qualifier
 *  only when this is non-empty — one guard, not two that can drift. Markers
 *  predating 2026-08-25 carry no trade/leak/query and are still sitting in the
 *  chat history; without this they would render "when they need a . things like
 *  **." into a block the operator copies straight to a client. */
function messageOne(d: GeoHandoffData): string {
  const trade = (d.trade ?? "").trim();
  const leak = (d.leak ?? "").trim();
  const query = (d.query ?? "").trim();
  if (d.alreadyBuilt || !trade || !leak || !query) return "";
  return (
    `while building your site we ran a quick check on the side.\n\n` +
    `we asked ${engineList(enginesOf(d))} what people ask when they need ${article(trade)} ${waSafe(trade)}. things like *${waSafe(query)}*.\n\n` +
    `${waSafe(leak)}\n\n` +
    `want me to send it over?`
  );
}

/** The payoff, sent with the screenshot. The score belongs HERE. */
function messageTwo(d: GeoHandoffData): string {
  const body = `${scoreClause(d)}\n\n${firmsClause(d.firms)}\n\ndo you know them?`;
  if (d.alreadyBuilt) {
    const trade = (d.trade ?? "").trim();
    return (
      `we ran a quick check on the side.\n\n` +
      `we asked ${engineList(enginesOf(d))} ${d.queries} questions people ask when they need ${article(trade)} ${waSafe(trade)}. that's ${d.slots} answers.\n\n` +
      body
    );
  }
  return `here you go.\n\n${d.queries} questions, asked on ${engineShorthand(enginesOf(d))}. that's ${d.slots} answers.\n\n${body}`;
}

export default function GeoHandoffCard({
  data,
  renderFile,
}: {
  data: GeoHandoffData;
  /** Supplied by AssistantBubble so the card can reuse the proven AssistantFile
   *  renderer (inline image + tap-to-zoom, fetch-on-click for the PDF) without
   *  importing it back from its own parent. */
  renderFile?: (path: string) => React.ReactNode;
}) {
  const msgOne = messageOne(data);
  const qualifies = msgOne !== "";
  const n = qualifies ? 2 : 1;
  return (
    <div className="handoff" role="group" aria-label="What to say — GEO audit handoff">
      <div className="handoff__label">What to say</div>
      <div className="handoff__card">
        <div className="handoff__lead">
          <ChartGlyph />
          <span>
            {qualifies
              ? "Send message 1 on its own. Only on yes, send message 2 with the screenshot."
              : "Site is already built — no qualifier. Send this with the screenshot."}
          </span>
        </div>

        {data.weakPitch && (
          <div className="handoff__msg">
            <div className="handoff__step">
              ⚠ The data undercuts this pitch — {data.weakPitch}
            </div>
          </div>
        )}

        {qualifies && (
          <GeoMessage step="Message 1 · the qualifier — no attachment" copyText={msgOne} />
        )}

        <div className="handoff__msg">
          <div className="handoff__step">Message {n} attachment · send this image</div>
          <div className="handoff__box">
            <div className="handoff__boxHead">
              <span className="handoff__hint">Tap to view full size · press and hold to save</span>
            </div>
            {data.imagePath && renderFile ? (
              <div className="handoff__file">{renderFile(data.imagePath)}</div>
            ) : (
              <div className="handoff__body" style={{ cursor: "default" }}>
                {data.image ?? "the full-result page"}
              </div>
            )}
          </div>
        </div>

        <GeoMessage
          step={qualifies ? "Message 2 · on yes, with the screenshot" : "Send this with the screenshot"}
          copyText={messageTwo(data)}
        />

        <div className="handoff__msg">
          <div className="handoff__step">The offer · see the playbook, after they reply</div>
        </div>

        {data.pdfUrl && (
          <GeoMessage step="Offer attachment · send this report link" copyText={data.pdfUrl} />
        )}

        {(data.pdfPath || data.pdf) && (
          <div className="handoff__msg">
            <div className="handoff__step">
              {data.pdfUrl ? "The same report, to preview before you send" : "Attach with the offer · the report"}
            </div>
            <div className="handoff__box">
              <div className="handoff__boxHead">
                <span className="handoff__hint">Tap to open or download, then attach in WhatsApp</span>
              </div>
              {data.pdfPath && renderFile ? (
                <div className="handoff__file">{renderFile(data.pdfPath)}</div>
              ) : (
                <div className="handoff__body" style={{ cursor: "default" }}>{data.pdf}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* One message row: label + copy-box. Mirrors HandoffMessage in
 * WhatsappHandoffCard; kept local so that proven card is left untouched. */
function GeoMessage({ step, copyText }: { step: string; copyText: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  function copy() {
    // copyTextToClipboard, not navigator.clipboard directly — some PWA WebViews
    // expose the Clipboard API and then reject it even during a user gesture,
    // which failed silently while still flashing "Copied". This is the card the
    // operator copies from on a phone, so a false confirmation is the worst
    // possible failure: he pastes the previous message to a client.
    void copyTextToClipboard(copyText).then((ok) => {
      if (!ok) return;
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    });
  }

  return (
    <div className="handoff__msg">
      <div className="handoff__step">{step}</div>
      <div className={`handoff__box ${copied ? "is-copied" : ""}`}>
        <div className="handoff__boxHead">
          <span className="handoff__hint">Tap to copy → paste in WhatsApp</span>
          <button
            type="button"
            className="handoff__copy"
            onClick={copy}
            aria-label={copied ? "Copied" : "Copy this message"}
            title={copied ? "Copied" : "Copy"}
          >
            {copied ? <CheckGlyph /> : <CopyGlyph />}
            <span>{copied ? "Copied" : "Copy"}</span>
          </button>
        </div>
        {/* Raw string, no <br> elements: .handoff__body is already
            white-space:pre-wrap, so newlines render on their own. Splitting into
            spans with <br> double-spaces every blank line and drags the source
            indentation into the bubble. */}
        <button type="button" className="handoff__body" onClick={copy} title="Tap to copy">
          {copyText}
        </button>
      </div>
    </div>
  );
}

/* Inline SVG glyphs — matches the app's naked-icon look, no icon-font dep. */
function ChartGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="6" />
      <rect x="13" y="8" width="3" height="10" />
      <rect x="19" y="5" width="2" height="13" />
    </svg>
  );
}
function CopyGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
